import { Box, Typography } from "@material-ui/core";
import React, { useState } from "react";
import {
  AddToCartButton,
  QuantitySelector,
  QuantityButton,
  QuantityValue
} from "./styles";
import useExpressDeliveryCart from "../../../hooks/use-express-delivery-cart";
import ExpressDeliveryLogo from "../../express-delivery/ExpressDeliveryLogo";
import { useCounter } from "react-use";
import { ProductType } from "../../../types/airgraft";
import useTranslate from "../../../hooks/use-translate";
import { getDefaultProductVariant } from "../../../helpers/product";

function sleep(ms) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

type Props = {
  product: ProductType;
  maxQuantity?: number;
};

export default function ProductQuantitySelector({
  product,
  maxQuantity
}: Props) {
  const t = useTranslate();
  const { toggleCartPanel, addItem } = useExpressDeliveryCart();
  const [quantity, { inc, dec }] = useCounter(1, maxQuantity || 100, 1);

  const productVariant = getDefaultProductVariant(product);

  const [isAdding, setAdding] = useState(false); // Button will show "Adding..." for few ms before adding to cart

  const handleMainButtonClick = async () => {
    setAdding(true);
    await sleep(400);
    setAdding(false);
    toggleCartPanel(true);
    addItem(
      {
        productVariantId: productVariant.originalId,
        metadata: {
          name: productVariant.title,
          slug: product.slug,
          productType: product.productType,
          brand: productVariant.brand.name,
          image: productVariant.featuredImage.url,
          thcGrams: productVariant.variantDetail?.coaReport?.weightGrams
        }
      },
      quantity
    );
  };

  const handleMinusButtonClick = () => {
    dec();
    toggleCartPanel(false);
  };

  const handlePlusButtonClick = () => {
    inc();
    toggleCartPanel(false);
  };

  const hasReachedMaxQuantity = quantity >= maxQuantity;

  return (
    <>
      <Box display="flex" alignItems="center">
        <QuantitySelector>
          <QuantityButton
            onClick={handleMinusButtonClick}
            className={quantity === 1 ? "disabled" : ""}
            disabled={isAdding}
          >
            -
          </QuantityButton>
          <QuantityValue>{quantity}</QuantityValue>
          <QuantityButton
            onClick={handlePlusButtonClick}
            disabled={isAdding || hasReachedMaxQuantity}
          >
            +
          </QuantityButton>
        </QuantitySelector>
        <AddToCartButton
          variant="contained"
          color="primary"
          className="express-delivery"
          onClick={handleMainButtonClick}
          disabled={isAdding}
          startIcon={
            isAdding ? null : (
              <ExpressDeliveryLogo height="12px" variant="truck" />
            )
          }
        >
          {isAdding ? t("shop.addingToCart") : t("shop.addToCart")}
        </AddToCartButton>
      </Box>
      {hasReachedMaxQuantity && (
        <Box mt={0.5}>
          <Typography color="error" variant="caption">
            {t("pdp.quantitySelector.maxReached", { maxQuantity })}
          </Typography>
        </Box>
      )}
    </>
  );
}
